import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Shield, Zap, Lock, Code, Users, BarChart3, Cloud, CheckCircle } from 'lucide-react'

interface LandingPageProps {
  onLoginClick: () => void
}

const FEATURES = [
  {
    icon: <Zap className="w-8 h-8" />,
    title: 'Lightning Fast',
    description: 'Deploy and manage apps in seconds with automated infrastructure.',
    color: 'from-yellow-400 to-orange-500',
  },
  {
    icon: <Shield className="w-8 h-8" />,
    title: 'Enterprise Security',
    description: 'OpenBao vault encryption, Authentik authentication, fine-grained access control.',
    color: 'from-green-400 to-emerald-500',
  },
  {
    icon: <Lock className="w-8 h-8" />,
    title: 'Access Control',
    description: 'Role-based permissions, OAuth2/OIDC, SSO integration for seamless auth.',
    color: 'from-blue-400 to-cyan-500',
  },
  {
    icon: <Code className="w-8 h-8" />,
    title: 'Developer Friendly',
    description: 'REST API, comprehensive documentation, multiple SDKs and examples.',
    color: 'from-purple-400 to-pink-500',
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: 'Team Collaboration',
    description: 'Share apps across teams, manage permissions, track deployments.',
    color: 'from-indigo-400 to-blue-500',
  },
  {
    icon: <BarChart3 className="w-8 h-8" />,
    title: 'Analytics & Insights',
    description: 'Track downloads, user engagement, and performance metrics.',
    color: 'from-red-400 to-pink-500',
  },
  {
    icon: <Cloud className="w-8 h-8" />,
    title: 'Multi-Platform',
    description: 'Android, iOS, and web apps in one unified platform.',
    color: 'from-teal-400 to-cyan-500',
  },
  {
    icon: <CheckCircle className="w-8 h-8" />,
    title: 'Scalable Infrastructure',
    description: 'From dev to enterprise, scales with your needs on self-hosted Coherence.',
    color: 'from-orange-400 to-red-500',
  },
]

const TECH_STACK = [
  { name: 'Python', icon: '🐍' },
  { name: 'React', icon: '⚛️' },
  { name: 'Docker', icon: '🐳' },
  { name: 'PostgreSQL', icon: '🐘' },
  { name: 'OpenBao', icon: '🔐' },
  { name: 'Authentik', icon: '🔑' },
  { name: 'Ceph', icon: '📦' },
  { name: 'Ollama', icon: '🤖' },
  { name: 'FastAPI', icon: '⚡' },
  { name: 'Proxmox', icon: '☁️' },
]

export default function LandingPage({ onLoginClick }: LandingPageProps) {
  const { data: appsData } = useQuery({
    queryKey: ['apps-count'],
    queryFn: async () => {
      const res = await fetch('/api/apps')
      if (!res.ok) return null
      return res.json()
    },
    staleTime: 5 * 60 * 1000,
  })

  const appCount = appsData?.apps?.length ?? null

  const HERO_STATS = [
    { label: 'Deployment Time', value: '< 30 min', subtext: 'Fully automated setup' },
    { label: 'Cost Savings', value: '95%', subtext: 'vs SaaS alternatives' },
    { label: 'Security Grade', value: 'A+', subtext: 'Enterprise hardened' },
  ]

  const PLATFORM_STATS = [
    { label: 'Apps Available', value: appCount !== null ? `${appCount}` : '—' },
    { label: 'Uptime SLA', value: '99.9%' },
    { label: 'Security Score', value: 'A+' },
    { label: 'Infrastructure', value: 'Self-Hosted' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className="backdrop-blur-md bg-slate-900/30 border-b border-purple-500/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                <Code className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                MSKAI AppStore
              </span>
            </div>
            <button
              onClick={onLoginClick}
              className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/50 flex items-center space-x-2"
            >
              <span>Sign In</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-block px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full">
              <span className="text-purple-200 text-sm font-semibold flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse inline-block" />
                <span>Enterprise-Grade App Distribution Platform</span>
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold text-white space-y-2">
              <div>Deploy Apps</div>
              <div className="bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent">
                with Confidence
              </div>
            </h1>

            <p className="text-xl text-slate-300 leading-relaxed">
              A complete, self-hosted app store platform with enterprise-grade security, automated
              deployment, and professional analytics. Built on the Coherence ecosystem.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                onClick={onLoginClick}
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-bold text-lg transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/50 flex items-center justify-center space-x-2"
              >
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="#features"
                className="px-8 py-4 border border-purple-400/50 hover:border-purple-400 text-purple-300 rounded-lg font-bold text-lg transition-all duration-300 hover:bg-purple-500/10 flex items-center justify-center"
              >
                Learn More
              </a>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-8">
              {HERO_STATS.map((stat) => (
                <div
                  key={stat.label}
                  className="border border-purple-500/20 rounded-lg p-4 bg-purple-500/5 backdrop-blur-sm"
                >
                  <p className="text-2xl font-bold text-purple-400">{stat.value}</p>
                  <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{stat.subtext}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl blur-3xl opacity-20 animate-pulse" />
            <div className="relative bg-slate-800/50 backdrop-blur-xl border border-purple-400/20 rounded-2xl p-8 shadow-2xl">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="h-4 bg-gradient-to-r from-purple-400/20 to-transparent rounded animate-pulse"
                    style={{ animationDelay: `${i * 0.1}s`, width: `${100 - i * 15}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Stats Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-y border-purple-500/20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {PLATFORM_STATS.map((item) => (
            <div key={item.label} className="text-center">
              <p className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {item.value}
              </p>
              <p className="text-slate-400 mt-2">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">Powerful Features</h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Everything you need to distribute, manage, and secure your applications
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="group relative p-6 rounded-xl border border-purple-500/10 bg-slate-800/30 hover:border-purple-400/30 transition-all duration-300"
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 rounded-xl transition-opacity duration-300`}
              />
              <div className="relative text-purple-400 mb-4 group-hover:text-pink-400 transition-colors duration-300">
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-y border-purple-500/20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">Built on Modern Technology</h2>
          <p className="text-xl text-slate-400">Enterprise-grade infrastructure and open-source tools</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {TECH_STACK.map((tech) => (
            <div
              key={tech.name}
              className="flex flex-col items-center justify-center p-6 rounded-lg bg-slate-800/30 border border-purple-500/10 hover:border-purple-400/30 transition-all duration-300 hover:bg-slate-800/50"
            >
              <span className="text-4xl mb-2">{tech.icon}</span>
              <span className="text-slate-300 font-semibold text-sm">{tech.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 p-12 shadow-2xl">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/30" />
          </div>
          <div className="relative text-center space-y-6">
            <h2 className="text-4xl font-bold text-white">Ready to Get Started?</h2>
            <p className="text-xl text-purple-100 max-w-2xl mx-auto">
              Sign in to access the full app store platform and start distributing your applications.
            </p>
            <button
              onClick={onLoginClick}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-purple-600 rounded-lg font-bold text-lg hover:bg-purple-50 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span>Sign In Now</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-purple-500/20 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-bold mb-4">Product</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#features" className="hover:text-purple-400 transition">Features</a></li>
                <li><a href="#" className="hover:text-purple-400 transition">Security</a></li>
                <li><a href="#" className="hover:text-purple-400 transition">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Apps</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="/harvey" className="hover:text-purple-400 transition">Harvey</a></li>
                <li><a href="/mmb" className="hover:text-purple-400 transition">MMB</a></li>
                <li><a href="/danai" className="hover:text-purple-400 transition">DANAI</a></li>
                <li><a href="/gc" className="hover:text-purple-400 transition">Ground Control</a></li>
                <li><a href="/famulus" className="hover:text-purple-400 transition">Famulus</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Resources</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="/coherence" className="hover:text-purple-400 transition">Coherence Engine</a></li>
                <li><a href="/privacy" className="hover:text-purple-400 transition">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-purple-400 transition">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Ecosystem</h3>
              <ul className="space-y-2 text-slate-400">
                <li><a href="/sentinel" className="hover:text-purple-400 transition">Sentinel</a></li>
                <li><a href="/symphony" className="hover:text-purple-400 transition">Symphony</a></li>
                <li><a href="/wb2" className="hover:text-purple-400 transition">WB2 Platform</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-purple-500/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-2 mb-4 md:mb-0">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                  <Code className="w-4 h-4 text-white" />
                </div>
                <span className="text-white font-bold">MSKAI AppStore</span>
              </div>
              <p className="text-slate-400 text-sm">
                © {new Date().getFullYear()} MSKAI. All rights reserved. Built on the Coherence ecosystem.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
